import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { BASE_URL } from '@/config/api';
import { Link as LinkIcon, Loader2, Users, Gift } from 'lucide-react';

const ReferralManager: React.FC = () => {
  const { toast } = useToast();
  const { token, user } = useAuth();
  const { t, locale } = useLanguage();

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return t('common.invalidDate');
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return t('common.invalidDate');
    }
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(date);
  };

  const referralLink = `https://thepoppyapp.com/register?referral=${user.referral}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast({
      title: t('referral.linkCopied'),
      description: t('referral.linkCopiedSuccessfully'),
    });
  };

  const { data: invitations = [], isLoading, refetch } = useQuery({
    queryKey: ['get-referrals', token],
    queryFn: async () => {
      const response = await fetch(`${BASE_URL}/api/referrals`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch invitations');
      }
      const data = await response.json();
      return data.referrals || [];
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <LinkIcon className="h-5 w-5" />
            {t('referral.title')}
          </CardTitle>
          <CardDescription>
            {t('referral.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="referral-link">{t('referral.yourInviteLink')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="referral-link"
                  type="text"
                  value={referralLink}
                  readOnly
                  className="pl-10"
                />
              </div>
              <Button onClick={handleCopyLink}>
                {t('referral.copyLink')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex flex-row items-center gap-2">
            <Gift className="h-6 w-6" />
            <CardTitle className="text-lg">{t('referral.sentInvitations')}</CardTitle>
          </div>
          <CardDescription>
            {t('referral.sentInvitationDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : invitations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mb-2" />
              <p>{t('referral.noInvitationsSentYet')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <p className="text-sm">{invitation.name}</p>
                    <Badge variant={invitation.is_subscribed ? "outline" : "destructive"}>
                      {invitation.is_subscribed ? t('referral.subscribed') + ' (' +  formatDate(invitation.updated_date) + ')' : t('referral.notSubscribed')}
                    </Badge>
                  </div>
                  <Badge variant="secondary">
                    {!invitation.is_subscribed ? t('referral.sent') : t('referral.freeMonth')} {!invitation.is_subscribed ? formatDate(invitation.updated_date) : ''}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralManager;
